/**
 * Wspólne pomocniki interakcji edytora układu tabeli (przycisk „Kolumny", menu nagłówka,
 * zmiana szerokości) — jedno miejsce zamiast kopii w każdym komponencie.
 */

import {
  type Ref,
  type RefCallback,
  type SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

/** Zatrzymuje bąbelkowanie zdarzenia React (klik/wciśnięcie w kontrolce nie dociera do kreatora). */
export function stopBubble(event: SyntheticEvent): void {
  event.stopPropagation();
}

/**
 * Składa kilka refów (funkcje i obiekty; `undefined` pomijany) w jeden ref-callback — dla elementu,
 * który ma naraz kilku odbiorców (np. własny ref do nasłuchu Esc + rejestracja portalu w oknie).
 * Zwraca funkcję sprzątającą (React 19 woła ją przy odpięciu zamiast przekazywania `null`):
 * refy-funkcje dostają wtedy `null` albo własne sprzątanie, refy-obiekty są zerowane.
 * Wynik memoizuj (`useMemo`) — nowa funkcja co render odpinałaby i podpinała refy za każdym razem.
 */
export function composeRefs<T>(...refs: ReadonlyArray<Ref<T> | undefined>): RefCallback<T> {
  return (node) => {
    const cleanups: Array<() => void> = [];
    for (const ref of refs) {
      if (typeof ref === 'function') {
        const cleanup = ref(node);
        cleanups.push(typeof cleanup === 'function' ? cleanup : () => ref(null));
      } else if (ref) {
        ref.current = node;
        cleanups.push(() => {
          ref.current = null;
        });
      }
    }
    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  };
}

/**
 * `onCloseAutoFocus` popovera: nie odbiera fokusu, gdy użytkownik zdążył go przenieść
 * (np. otworzył inne menu — jego treść ma już fokus). Bez tego zamykany popover „wciągałby"
 * fokus z powrotem na swój przycisk. Przy fokusie na `<body>` (zamknięcie Esc) zostaje
 * domyślne zachowanie Radix: fokus wraca na przycisk popovera.
 */
export function keepFocusIfMoved(event: Event): void {
  const active = document.activeElement;
  if (active !== null && active !== document.body) event.preventDefault();
}

/**
 * Połyka dokładnie JEDEN najbliższy klik (faza przechwytywania na `document`) — jak dnd-kit
 * po przeciągnięciu. Po gestach zmiany szerokości puszczenie przycisku myszy może wypaść
 * poza uchwytem, a przeglądarka wysyła wtedy klik do wspólnego przodka, który bąbelkuje do
 * kreatora i zaznacza element. Gdy klik nie nadejdzie, nasłuch znika w następnym makrozadaniu.
 */
export function suppressNextClick(): void {
  const handleClick = (event: MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    release();
  };
  const timer = window.setTimeout(() => release(), 0);
  function release(): void {
    window.clearTimeout(timer);
    document.removeEventListener('click', handleClick, true);
  }
  document.addEventListener('click', handleClick, true);
}

/**
 * Ref z najnowszą wartością (aktualizowany po każdym renderze) — dla handlerów wołanych
 * poza cyklem renderu (gesty na `document`, efekty), które nie mogą zamykać się na stare propsy.
 * Aktualizacja w efekcie układu: dzięki temu ref jest świeży przed wszystkimi efektami
 * zwykłymi (także dzieci) i przed następnym zdarzeniem.
 */
export function useLatestRef<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Woła `onClosed` w chwili, gdy `open` zmienia się z `true` na `false` — także wtedy, gdy
 * zamknięcie wymusiła rodzic (otwarto inne menu, zaczęto zmianę szerokości), czyli bez
 * udziału `onOpenChange` Radix.
 */
export function useOnClosed(open: boolean, onClosed: () => void): void {
  const latest = useLatestRef(onClosed);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) latest.current();
    wasOpen.current = open;
  }, [open, latest]);
}
